/**
 * The rise preview — what the amount being typed would do to the selected
 * category, shown before Save (SPEC.md §3.1).
 *
 * **Arithmetic on the number in front of you, never a projection.** It answers
 * "if I save this, where does Food stand?", not "where will Food end the
 * month". Its copy says "rises to", never "will".
 *
 * Money stays integer cents (INV-1); the ratios exist only to place the
 * preview line on the water and are never turned back into money. The
 * percentage is floored, so a category at 99.6% does not read as full.
 */

import { type Cents, cents, ratio } from "./money";
import { type Mark, crossedMarks } from "./thresholds";

const PERCENT = 100;

export interface RisePreview {
  hasBudget: boolean;
  spentAfter: Cents;
  /** budget − spent, before and after. Negative is over. Null without a budget. */
  leftBefore: Cents | null;
  leftAfter: Cents | null;
  /** Spent as a share of the budget, 0..∞. Zero without a budget. */
  ratioBefore: number;
  ratioAfter: number;
  /** Floored whole percentages, or null without a budget. */
  percentBefore: number | null;
  percentAfter: number | null;
  overBefore: boolean;
  overAfter: boolean;
  /** The 80% / 100% marks this amount would carry the category across. */
  crossed: Mark[];
}

export function risePreview(standing: { budget: Cents | null; spent: Cents }, amount: Cents): RisePreview {
  const { budget, spent } = standing;
  const spentAfter = cents(spent + amount);
  if (budget === null || budget <= 0) {
    return {
      hasBudget: false,
      spentAfter,
      leftBefore: null,
      leftAfter: null,
      ratioBefore: 0,
      ratioAfter: 0,
      percentBefore: null,
      percentAfter: null,
      overBefore: false,
      overAfter: false,
      crossed: [],
    };
  }
  const leftBefore = cents(budget - spent);
  const leftAfter = cents(budget - spentAfter);
  return {
    hasBudget: true,
    spentAfter,
    leftBefore,
    leftAfter,
    ratioBefore: ratio(spent, budget),
    ratioAfter: ratio(spentAfter, budget),
    // Integer division by construction: whole cents × 100, floored by the budget.
    percentBefore: Math.floor((spent * PERCENT) / budget),
    percentAfter: Math.floor((spentAfter * PERCENT) / budget),
    overBefore: leftBefore < 0,
    overAfter: leftAfter < 0,
    crossed: crossedMarks(spent, spentAfter, budget),
  };
}
